import { memo, useEffect, useState } from 'react';
import type { IJournal } from '../../../../common/validation/episodesAndJournal';
import type { ProfessionalSummaryDataSectionLoadResult } from '../../../utils/professionalSummaryDataSectionLoadResult';
import type { IAllergy } from '../../interfaces/allergy';
import type { LabResult } from '../../interfaces/labresult';
import type { IEpisode } from '../../interfaces/patientEpisode';
import type { IProcedureGetOne } from '../../interfaces/procedureGetOne';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import { ProfessionalSummaryDataType } from '../../../../common/enums/professionalSummaryDataType';
import { translateKey } from '../../../translate';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';
import {
    FormDivider,
    HeaderDivider,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';
import { prepareAndSetJournalEntries } from '../customControls/utils/v1/episodeJournals/episodeJournals';
import { JournalEntriesRead } from './JournalEntriesRead';
import { ProfessionalSummaryDataSection } from './ProfessionalSummaryDataSection';
import { ProfessionalSummaryDataTable } from './ProfessionalSummaryDataTable';

interface IProfessionalSummaryDataReadProps {
    onContentLoaded?: (hasContent: boolean) => Promise<void>;
    procedure?: IProcedureGetOne;
    request: IRequestGetOne;
}

export const ProfessionalSummaryDataRead = memo(function (props: IProfessionalSummaryDataReadProps) {
    const [isAllergiesOpen, setIsAllergiesOpen] = useState(false);
    const [isDiagnosticDeterminationsOpen, setIsDiagnosticDeterminationsOpen] = useState(false);
    const [isEpisodesAndJournalsOpen, setIsEpisodesAndJournalsOpen] = useState(false);

    const [labResults, setLabResults] = useState<LabResult[]>();
    const [selectedLabResultsCount, setSelectedLabResultsCount] = useState<number>(0);
    const [labResultsLoadResult, setLabResultsLoadResult] = useState<ProfessionalSummaryDataSectionLoadResult>();

    const [allergies, setAllergies] = useState<IAllergy[]>();
    const [selectedAllergiesCount, setSelectedAllergiesCount] = useState<number>(0);
    const [allergiesLoadResult, setAllergiesLoadResult] = useState<ProfessionalSummaryDataSectionLoadResult>();

    const [episodes, setEpisodes] = useState<IEpisode[]>();
    const [requestJournals, setRequestJournals] = useState<IJournal[]>([]);
    const [selectedEpisodesCount, setSelectedEpisodesCount] = useState<number>(0);
    const [episodesLoadResult, setEpisodesLoadResult] = useState<ProfessionalSummaryDataSectionLoadResult>();

    const abortableFetch = useAbortableFetch();
    const [isHisDataAvailable, setIsHisDataAvailable] = useState<boolean>();

    useEffect(() => {
        loadLabResults();
        loadAllergies();
        loadEpisodesAndJournal();
        loadIsHisDataAvailable();
    }, []);

    if (!isHisDataAvailable) {
        return null;
    }

    const isPsdEmpty = !selectedEpisodesCount && !selectedAllergiesCount && !selectedLabResultsCount;
    const isPsdHidden = !isHisDataAvailable || isPsdEmpty;

    return (
        <>
            {!isPsdHidden && (
                <>
                    <HeaderDivider text={translateKey('professionalSummary.professionalSummary')} />

                    {selectedAllergiesCount !== 0 && (
                        <ProfessionalSummaryDataSection
                            isOpen={isAllergiesOpen}
                            loadResult={allergiesLoadResult}
                            onToggle={() => setIsAllergiesOpen(!isAllergiesOpen)}
                            professionalSummaryDataType={ProfessionalSummaryDataType.allergies}
                            readOnly
                            selectionCount={selectedAllergiesCount}
                        >
                            {allergies && allergies.length > 0 && (
                                <ProfessionalSummaryDataTable isRead={true}>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>{translateKey('allergies.displayName')}</TableHeader>
                                            <TableHeader>{translateKey('allergies.description')}</TableHeader>
                                            <TableHeader align="right">{translateKey('common.startDate')}</TableHeader>
                                            <TableHeader align="right">{translateKey('common.endDate')}</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allergies.map((allergy) => {
                                            return (
                                                <TableRow
                                                    highlight
                                                    key={allergy.id}
                                                >
                                                    <TableData>{allergy.displayName}</TableData>
                                                    <TableData>{allergy.comment}</TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <NullableFormattedDate value={allergy.startDate} />
                                                    </TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <NullableFormattedDate value={allergy.endDate} />
                                                    </TableData>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </ProfessionalSummaryDataTable>
                            )}
                        </ProfessionalSummaryDataSection>
                    )}

                    {selectedLabResultsCount !== 0 && (
                        <ProfessionalSummaryDataSection
                            isOpen={isDiagnosticDeterminationsOpen}
                            loadResult={labResultsLoadResult}
                            onToggle={() => setIsDiagnosticDeterminationsOpen(!isDiagnosticDeterminationsOpen)}
                            professionalSummaryDataType={ProfessionalSummaryDataType.diagnosticDeterminations}
                            readOnly
                            selectionCount={selectedLabResultsCount}
                        >
                            {labResults && labResults.length > 0 && (
                                <ProfessionalSummaryDataTable isRead={true}>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>{translateKey('labResults.description')}</TableHeader>
                                            <TableHeader>{translateKey('labResults.valueValue')}</TableHeader>
                                            <TableHeader align="right">
                                                {translateKey('labResults.valueUnit')}
                                            </TableHeader>
                                            <TableHeader align="right">{translateKey('common.date')}</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {labResults.map((labResult) => {
                                            return (
                                                <TableRow
                                                    highlight
                                                    key={labResult.id}
                                                >
                                                    <TableData>{labResult.codeDisplayName}</TableData>
                                                    <TableData>{labResult.valueValue}</TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        {labResult.valueUnit}
                                                    </TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <NullableFormattedDate value={labResult.startDate} />
                                                    </TableData>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </ProfessionalSummaryDataTable>
                            )}
                        </ProfessionalSummaryDataSection>
                    )}

                    {selectedEpisodesCount !== 0 && (
                        <ProfessionalSummaryDataSection
                            isOpen={isEpisodesAndJournalsOpen}
                            loadResult={episodesLoadResult}
                            onToggle={() => setIsEpisodesAndJournalsOpen(!isEpisodesAndJournalsOpen)}
                            professionalSummaryDataType={ProfessionalSummaryDataType.episodesAndJournals}
                            readOnly
                            selectionCount={selectedEpisodesCount}
                        >
                            {episodes && episodes.length > 0 && (
                                <ProfessionalSummaryDataTable isRead={true}>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>{translateKey('episodes.displayName')}</TableHeader>
                                            <TableHeader>{translateKey('episodes.description')}</TableHeader>
                                            <TableHeader align="right">{translateKey('episodes.icpc')}</TableHeader>
                                            <TableHeader align="right">{translateKey('common.startDate')}</TableHeader>
                                            <TableHeader align="right">{translateKey('common.endDate')}</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {episodes.map((episode) => {
                                            return (
                                                <TableRow
                                                    highlight
                                                    key={episode.id}
                                                >
                                                    <TableData>{episode.valueDisplayName}</TableData>
                                                    <TableData>{episode.comment}</TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        {episode.statusCode}
                                                    </TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <NullableFormattedDate value={episode.startDate} />
                                                    </TableData>
                                                    <TableData
                                                        align="right"
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        <NullableFormattedDate value={episode.endDate} />
                                                    </TableData>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </ProfessionalSummaryDataTable>
                            )}

                            {episodes && requestJournals.length > 0 ? (
                                <JournalEntriesRead journalEntries={requestJournals} />
                            ) : null}
                        </ProfessionalSummaryDataSection>
                    )}

                    <FormDivider className="mt-2 mb-4" />
                </>
            )}
        </>
    );

    async function loadLabResults() {
        let isLoadCompleted = false;
        let loadFailed = false;

        try {
            const results = await abortableFetch.fetchJson(
                `/api/requests/${props.request.id}/requestLabResults`,
                'GET'
            );
            setLabResults(results.json.items);
            setSelectedLabResultsCount(results.json.total);
        } catch (error) {
            loadFailed = true;
        }

        isLoadCompleted = true;
        setLabResultsLoadResult({ isLoadCompleted, loadFailed });
    }

    async function loadAllergies() {
        let isLoadCompleted = false;
        let loadFailed = false;

        try {
            const results = await abortableFetch.fetchJson(`/api/requests/${props.request.id}/requestAllergies`, 'GET');
            setAllergies(results.json.items);
            setSelectedAllergiesCount(results.json.total);
        } catch (error) {
            loadFailed = true;
        }

        isLoadCompleted = true;
        setAllergiesLoadResult({ isLoadCompleted, loadFailed });
    }

    async function loadEpisodesAndJournal() {
        let isLoadCompleted = false;
        let loadFailed = false;

        try {
            const requestEpisodesResponse = await abortableFetch.fetchJson(
                `/api/requests/${props.request.id}/requestEpisodes`,
                'GET'
            );

            if (requestEpisodesResponse.json.items) {
                setEpisodes(requestEpisodesResponse.json.items);
                setSelectedEpisodesCount(requestEpisodesResponse.json.items.length);
            }

            const requestJournalEntriesResponse = await abortableFetch.fetchJson(
                `/api/requests/${props.request.id}/requestJournalEntries`,
                'GET'
            );

            if (requestJournalEntriesResponse.json.items?.length > 0) {
                const journals: IJournal[] = prepareAndSetJournalEntries(
                    requestJournalEntriesResponse.json.items,
                    requestEpisodesResponse.json.items
                ).toArray();

                setRequestJournals(journals);
            }
        } catch (error) {
            loadFailed = true;
        }

        isLoadCompleted = true;
        setEpisodesLoadResult({ isLoadCompleted, loadFailed });
    }

    async function loadIsHisDataAvailable() {
        const result = await abortableFetch.fetchJson(
            `/api/patients/${props.request.patientId}/hisDataAvailability?requestId=${props.request.id}`,
            'GET'
        );
        setIsHisDataAvailable(result.json?.result === true);
    }
});
